import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useSessionStorage } from 'usehooks-ts';
import { DateFilters } from 'shared/components/table-filter-chips/models/date-filters';
import { FilterChipOption } from 'shared/components/table-filter-chips/multi-value-filter-chip/models/filter-chip-option';
import { TableFiltering } from 'shared/components/table/hooks/table-filtering';
import FilterSessionStorageKeys from 'shared/constants/filterSessionStorageKeys';
import { ScenarioType } from 'shared/enums/scenario-type';
import SelectedOrganisationTreeNode from 'shared/features/organisation/models/selected-organisation-tree-node';
import KeyValuePair from 'shared/models/key-value-pair';

import ReadResidentsChangeLogFilter from 'features/residents/models/read-residents-change-log-filter';
import { ResidentsChangeLogFilterValueType } from 'features/residents/models/read-residents-change-log-filter-value';
import ReadResidentsChangeLogQuery from 'features/residents/models/read-residents-change-log-query';
import ResidentChangeLog from 'features/residents/models/resident-change-log';
import { ResidentChangeLogCategory } from 'features/residents/models/resident-change-log-category';
import { ResidentChangeLogType } from 'features/residents/models/resident-change-log-type';
import { useReadResidentsChangeLogFilters } from 'features/residents/resident-change-logs/providers/residents-change-log-filters-queries';

export type ResidentsChangeLogFiltersState = {
  getResidentsChangeLogQuery: ReadResidentsChangeLogQuery;
  searchBar: string;
  changeSearchBarValue: (searchBarValue: string) => void;
  organisationUnits: SelectedOrganisationTreeNode[];
  changeSelectedOrganisationUnits: (selectedOrganisationUnits: SelectedOrganisationTreeNode[]) => void;
  clearFilters: () => void;
  changeFilters: (filterValueType: ResidentsChangeLogFilterValueType, keys: string[]) => void;
  categoryOptions: FilterChipOption[];
  selectedCategoryOptions: FilterChipOption[];
  scenarioOptions: FilterChipOption[];
  selectedScenarioOptions: FilterChipOption[];
  typeOptions: FilterChipOption[];
  selectedTypeOptions: FilterChipOption[];
  employeeOptions: FilterChipOption[];
  selectedEmployeeOptions: FilterChipOption[];
  deviceOptions: FilterChipOption[];
  selectedDeviceOptions: FilterChipOption[];
  muteScenariosOptions: KeyValuePair[];
  selectedMuteScenariosOption: KeyValuePair | undefined;
  setDateFilters: (newDateFilter: DateFilters) => void;
  dateFilter: DateFilters;
  refresh: () => void;
};

type Props = {
  table: TableFiltering<ResidentChangeLog>;
};

const useResidentsChangeLogFiltersState = ({ table }: Props): ResidentsChangeLogFiltersState => {
  const { t } = useTranslation('residentsChangeLog');

  const defaultDateFilters: DateFilters = {
    dateFrom: moment().subtract(30, 'days').endOf('day'),
    dateTo: moment().endOf('day'),
  };

  const [filtersFromSessionStorage, setFiltersFromSessionStorage] = useSessionStorage<ReadResidentsChangeLogFilter[]>(
    FilterSessionStorageKeys.RESIDENT_SETTING_CHANGE_LOG_FILTERS,
    [],
  );

  const [dateFilter, setDateFilter] = useSessionStorage<DateFilters>(
    FilterSessionStorageKeys.RESIDENT_SETTING_CHANGE_LOG_DATE_FILTERS,
    defaultDateFilters,
  );

  const [organisationUnits, setOrganisationUnits] = useSessionStorage<SelectedOrganisationTreeNode[]>(
    FilterSessionStorageKeys.RESIDENT_SETTING_CHANGE_LOG_ORG_UNITS,
    [],
  );

  const [searchBar, setSearchBar] = useSessionStorage<string>(FilterSessionStorageKeys.RESIDENT_SETTING_CHANGE_LOG_SEARCH_BAR, '');

  const { data, refetch } = useReadResidentsChangeLogFilters({
    dateFrom: dateFilter.dateFrom,
    dateTo: dateFilter.dateTo,
  });

  const refresh = () => {
    refetch();
  };

  const getResidentsChangeLogQuery = useMemo(
    () =>
      ({
        pagination: table.currentPagination[0],
        searchQuery: searchBar,
        filters: filtersFromSessionStorage,
        dateFrom: dateFilter.dateFrom,
        dateTo: dateFilter.dateTo,
      }) as ReadResidentsChangeLogQuery,
    [table.currentPagination, searchBar, filtersFromSessionStorage, dateFilter],
  );

  const changeFilters = (filterValueType: ResidentsChangeLogFilterValueType, keys: string[]): void => {
    const newFilter = {
      filterValueType,
      values: keys,
    };

    let updatedFiltersList = [...filtersFromSessionStorage];

    const existingIndex = updatedFiltersList.findIndex((x) => x.filterValueType === filterValueType);
    if (existingIndex !== -1) {
      updatedFiltersList[existingIndex] = newFilter;
    } else {
      updatedFiltersList.push(newFilter);
    }

    if (filterValueType === ResidentsChangeLogFilterValueType.Category) {
      const categoryToFilterTypeMap: Partial<Record<ResidentChangeLogCategory, ResidentsChangeLogFilterValueType>> = {
        [ResidentChangeLogCategory.Device]: ResidentsChangeLogFilterValueType.Device,
        [ResidentChangeLogCategory.Scenario]: ResidentsChangeLogFilterValueType.Scenario,
        [ResidentChangeLogCategory.MuteScenarios]: ResidentsChangeLogFilterValueType.MuteScenarios,
      };

      const activeDependentFilterTypes = new Set(
        keys
          .map((category) => categoryToFilterTypeMap[category as ResidentChangeLogCategory])
          .filter((type): type is ResidentsChangeLogFilterValueType => type !== undefined),
      );

      updatedFiltersList = updatedFiltersList.filter((filter) => {
        const isDependentFilter = Object.values(categoryToFilterTypeMap).includes(filter.filterValueType);
        if (isDependentFilter) {
          return activeDependentFilterTypes.has(filter.filterValueType);
        }
        return true;
      });
    }

    setFiltersFromSessionStorage(updatedFiltersList.filter((x) => x.values.length > 0));
    table.resetPaging();
  };

  const changeSelectedOrganisationUnits = (selectedOrganisationUnits: SelectedOrganisationTreeNode[]): void => {
    setOrganisationUnits(selectedOrganisationUnits);
    changeFilters(
      ResidentsChangeLogFilterValueType.Location,
      selectedOrganisationUnits.map((x) => x.id),
    );
  };

  const clearFilters = (): void => {
    setFiltersFromSessionStorage([]);
    setOrganisationUnits([]);
    setDateFilters(defaultDateFilters);
    setSearchBar('');
    table.resetPaging();
  };

  const changeSearchBarValue = (searchBarValue: string): void => {
    setSearchBar(searchBarValue);
    table.resetPaging();
  };

  const getSelectedOptions = (options: FilterChipOption[], filterType: ResidentsChangeLogFilterValueType): FilterChipOption[] => {
    const activeFilter = filtersFromSessionStorage.find((f) => f.filterValueType === filterType);
    return activeFilter ? options.filter((x) => activeFilter.values.includes(x.key)) : [];
  };

  const categoryOptions = (data?.categoryTypeFilterOptions ?? [])
    .filter((category) => category !== ResidentChangeLogCategory.Unknown)
    .map((value) => ({
      key: value,
      value: [value],
      label: t(`filters.categoryFilterValues.${value}`),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const selectedCategoryOptions = getSelectedOptions(categoryOptions, ResidentsChangeLogFilterValueType.Category);

  const scenarioOptions = (data?.scenarioTypeFilterOptions ?? [])
    .filter((scenario) => scenario !== ScenarioType.None)
    .map((value) => ({
      key: value,
      value: [value],
      label: t(`filters.scenarioFilterValues.${value}`),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const selectedScenarioOptions = getSelectedOptions(scenarioOptions, ResidentsChangeLogFilterValueType.Scenario);

  const typeOptions = (data?.changeTypeFilterOptions ?? [])
    .filter((type) => type !== ResidentChangeLogType.Unknown)
    .map((value) => ({
      key: value,
      value: [value],
      label: t(`residentsChangeLogTable.type.${value}`),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const selectedTypeOptions = getSelectedOptions(typeOptions, ResidentsChangeLogFilterValueType.Change);

  const employeeOptions = (data?.employeeTypeFilterOptions ?? [])
    .map((value) => ({
      key: value,
      value: [value],
      label: value,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const selectedEmployeeOptions = getSelectedOptions(employeeOptions, ResidentsChangeLogFilterValueType.Employee);

  const deviceOptions = (data?.deviceTypeFilterOptions ?? [])
    .map((value) => ({
      key: value,
      value: [value],
      label: value,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const selectedDeviceOptions = getSelectedOptions(deviceOptions, ResidentsChangeLogFilterValueType.Device);

  const muteScenariosOptions = useMemo<KeyValuePair[]>(
    () => [
      { key: 'Absence', value: t('filters.muteScenariosFilterValues.mutedAbsence') },
      { key: 'Visitor', value: t('filters.muteScenariosFilterValues.mutedVisitor') },
    ],
    [t],
  );

  const selectedMuteScenariosOption = muteScenariosOptions.find((option) =>
    (filtersFromSessionStorage.find((f) => f.filterValueType === ResidentsChangeLogFilterValueType.MuteScenarios)?.values ?? []).includes(
      option.key,
    ),
  );

  const setDateFilters = (newDateFilter: DateFilters) => {
    if (!newDateFilter.dateFrom?.isValid() || !newDateFilter.dateTo?.isValid()) {
      return;
    }

    setDateFilter((prevState) => ({
      ...prevState,
      dateFrom: newDateFilter.dateFrom,
      dateTo: newDateFilter.dateTo,
    }));
  };

  return {
    searchBar,
    changeSearchBarValue,
    clearFilters,
    getResidentsChangeLogQuery,
    organisationUnits,
    changeSelectedOrganisationUnits,
    changeFilters,
    categoryOptions,
    selectedCategoryOptions,
    scenarioOptions,
    selectedScenarioOptions,
    typeOptions,
    selectedTypeOptions,
    employeeOptions,
    selectedEmployeeOptions,
    deviceOptions,
    selectedDeviceOptions,
    muteScenariosOptions,
    selectedMuteScenariosOption,
    setDateFilters,
    dateFilter,
    refresh,
  };
};

export default useResidentsChangeLogFiltersState;
